import { useEffect, useRef, useState } from "react";
import { Box, Typography, IconButton, Button } from "@mui/material";
import { useDispatch } from "react-redux";
import {
  updateAttachmentsRequest,
  uploadTempFileRequest,
} from "../../../../utils/APICalls";
import { getOrderAttachmentsRequest } from "../../../../redux/orders/slice";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import Loading from "../../../../components/loading/loading";
import ErrorNotification from "../../../../components/error-notification/error-notification";

const createAttachmentRowId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

export default function EditInspectorAttachments({
  attachments,
  orderId,
  closeEdit,
}: any) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dispatch = useDispatch();

  const [editAttachments, setEditAttachments] = useState<any[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<{ [key: string]: File }>(
    {}
  );
  const [uploadError, setUploadError] = useState(false);
  const [uploadErrorMessage, setUploadErrorMessage] = useState("");
  const [showLoader, setShowLoader] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (attachments) {
      const mappedAttachments = attachments.map((att: any) => ({
        id: createAttachmentRowId(),
        name: att.attachmentName || "Unknown File",
        size: att.size || "Unknown",
        isUploaded: true,
        fileKey: att.attachmentKey,
        isExisting: true,
      }));
      setEditAttachments(mappedAttachments);
    }
  }, [attachments]);

  const onSave = async () => {
    setShowLoader(true);
    const existingAttachments = editAttachments
      .filter((att) => att.isExisting && att.fileKey)
      .map((att) => ({
        attachmentKey: att.fileKey,
      }));

    const newUploadedAttachments = editAttachments
      .filter((att) => !att.isExisting && att.isUploaded && att.fileKey)
      .map((att) => ({
        attachmentKey: att.fileKey,
      }));

    const payload = {
      attachments: existingAttachments,
      newAttachments: newUploadedAttachments,
    };

    try {
      const response = await updateAttachmentsRequest(orderId, payload);
      setShowLoader(false);
      if (response?.status === 204) {
        dispatch(getOrderAttachmentsRequest({ orderId }));
        closeEdit();
      } else if (response?.status === 400) {
        setShowErrorNotification(true);
        setErrorMessage(response?.data?.detail);
      }
    } catch (error) {
      setShowLoader(false);
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      setUploadError(false);
      setUploadErrorMessage("");

      // Validate file type
      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];

      if (!allowedTypes.includes(file.type)) {
        setUploadError(true);
        setUploadErrorMessage(
          "Unsupported file type. Only PDF, DOC, DOCX, XLSX, JPG, or PNG files are allowed."
        );
        return;
      }

      if (file.size > 5000000) {
        setUploadError(true);
        setUploadErrorMessage("File size exceeds 5MB limit.");
        return;
      }

      const newAttachment = {
        id: createAttachmentRowId(),
        name: file.name,
        size: `${(file.size / 1024).toFixed(0)}kb`,
        isUploaded: false,
        isUploading: true,
        isExisting: false,
      };

      setEditAttachments((prev) => [...prev, newAttachment]);
      setSelectedFiles((prev) => ({ ...prev, [newAttachment.id]: file }));

      try {
        const response = await uploadTempFileRequest(file);

        if (response && response.data) {
          setUploadError(false);
          setUploadErrorMessage("");
          setEditAttachments((prev) =>
            prev.map((att) =>
              att.id === newAttachment.id
                ? {
                    ...att,
                    isUploaded: true,
                    fileKey: response.data,
                    isUploading: false,
                  }
                : att
            )
          );
        } else {
          setEditAttachments((prev) =>
            prev.filter((att) => att.id !== newAttachment.id)
          );
          setSelectedFiles((prev) => {
            const newFiles = { ...prev };
            delete newFiles[newAttachment.id];
            return newFiles;
          });
          setUploadError(true);
          setUploadErrorMessage(
            response?.data?.detail || "Failed to upload file. Please try again."
          );
        }
      } catch (error) {
        setEditAttachments((prev) =>
          prev.filter((att) => att.id !== newAttachment.id)
        );
        setSelectedFiles((prev) => {
          const newFiles = { ...prev };
          delete newFiles[newAttachment.id];
          return newFiles;
        });
        setUploadError(true);
        setUploadErrorMessage("Failed to upload file. Please try again.");
      }
    }
  };

  const handleFileDelete = (attachmentId: string) => {
    if (selectedFiles[attachmentId]) {
      setSelectedFiles((prev) => {
        const newFiles = { ...prev };
        delete newFiles[attachmentId];
        return newFiles;
      });
    }
    setEditAttachments((prev) => prev.filter((att) => att.id !== attachmentId));
  };

  const renderUploadArea = () => {
    return (
      <>
        <Box
          sx={{
            height: 80,
            border: "1px dashed rgba(0, 0, 0, 0.12)",
            position: "relative",
            display: "flex",
            alignItems: "center",
            p: 0.8,
            cursor: "pointer",
            opacity: 1,
            pointerEvents: "auto",
          }}
          onClick={() => fileInputRef.current?.click()}
        >
          <UploadFileIcon sx={{ ml: 5 }} color={"primary"} />
          <Box ml={2}>
            <Box sx={{ display: "flex" }}>
              <Typography
                color={"primary"}
                sx={{ textDecoration: "underline" }}
              >
                Click to upload
              </Typography>
              <Typography sx={{ ml: 1 }}>or drag and drop</Typography>
            </Box>
            <Typography sx={{ color: "rgba(0, 0, 0, 0.6)" }}>
              PDF, DOC, DOCX, XLSX, JPG or PNG (max. 5MB)
            </Typography>
          </Box>
        </Box>
        <input
          ref={fileInputRef}
          style={{
            display: "none",
          }}
          type="file"
          accept=".pdf,.doc,.docx,.xlsx,.jpg,.jpeg,.png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,image/jpeg,image/jpg,image/png"
          onChange={handleFileChange}
          onClick={(event) => {
            (event.target as HTMLInputElement).value = "";
          }}
        />
      </>
    );
  };

  const renderFileItem = (attachment: any) => {
    if (attachment.isUploaded) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            border: "1.5px solid #4caf50",
            p: 0.8,
            borderRadius: 1,
            backgroundColor: "#F7FAF7",
            mr: 2,
          }}
        >
          <Box display={"flex"} alignItems={"center"}>
            <AttachFileOutlinedIcon color="primary" />
            <Box sx={{ ml: 1.5 }}>
              <Typography>{attachment.name}</Typography>
              <Typography sx={{ color: "rgba(0, 0, 0, 0.6)" }}>
                {attachment.size !== "Unknown" ? `${attachment.size} • ` : ""}
                Complete
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CheckCircleOutlineIcon color="success" sx={{ mr: 2 }} />
            <IconButton
              aria-label="delete"
              onClick={() => handleFileDelete(attachment.id)}
            >
              <DeleteOutlineIcon color="error" />
            </IconButton>
          </Box>
        </Box>
      );
    } else if (attachment.isUploading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            border: "1.5px solid #ff9800",
            p: 0.8,
            borderRadius: 1,
            backgroundColor: "#fff3e0",
            mr: 2,
          }}
        >
          <Box display={"flex"} alignItems={"center"}>
            <AttachFileOutlinedIcon color="primary" />
            <Box sx={{ ml: 1.5 }}>
              <Typography>{attachment.name}</Typography>
              <Typography sx={{ color: "rgba(0, 0, 0, 0.6)" }}>
                {attachment.size} • Uploading
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              aria-label="delete"
              onClick={() => handleFileDelete(attachment.id)}
            >
              <RemoveCircleOutlineIcon color="error" />
            </IconButton>
          </Box>
        </Box>
      );
    } else {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            border: "1.5px solid #4caf50",
            p: 0.8,
            borderRadius: 1,
            backgroundColor: "#F7FAF7",
            mr: 2,
          }}
        >
          <Box display={"flex"} alignItems={"center"}>
            <AttachFileOutlinedIcon color="primary" />
            <Box sx={{ ml: 1.5 }}>
              <Typography>{attachment.name}</Typography>
              <Typography>{attachment.size}</Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              aria-label="delete"
              onClick={() => handleFileDelete(attachment.id)}
            >
              <RemoveCircleOutlineIcon color="error" />
            </IconButton>
          </Box>
        </Box>
      );
    }
  };

  return (
    <>
      <Box
        sx={{
          mx: 2,
          my: 3,
          border: "1px solid #C4C4C4",
          borderRadius: 1,
          background: "#FAFAFA",
        }}
      >
        <Typography sx={{ mt: 2, mx: 2 }} variant="h5">
          Inspector Attachments
        </Typography>

        <Typography sx={{ ml: 2, mr: 12, mt: 1 }}>
          Upload documents required to be completed and submitted with this
          inspection.
        </Typography>

        <Box sx={{ mx: 2, my: 3 }}>
          <Typography>
            <b>Document</b>
          </Typography>
          {editAttachments.map((attachment) => (
            <Box
              key={attachment?.attachmentKey}
              sx={{
                mt: 1,
              }}
            >
              {renderFileItem(attachment)}
            </Box>
          ))}
          <Box sx={{ py: 1, mt: 1 }}>
            {renderUploadArea()}
            {uploadError && (
              <Typography sx={{ fontSize: 12, mt: 0.5 }} color="error">
                {uploadErrorMessage}
              </Typography>
            )}
          </Box>
        </Box>
        <Box
          sx={{
            mr: 2,
            mb: 2,
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          <Button variant="outlined" sx={{ mr: 2 }} onClick={closeEdit}>
            Cancel
          </Button>
          <Button variant="contained" onClick={onSave}>
            Save Changes
          </Button>
        </Box>
      </Box>
      {showErrorNotification && (
        <ErrorNotification
          show={showErrorNotification}
          onClose={() => setShowErrorNotification(false)}
          title={errorMessage}
        />
      )}
      {showLoader && <Loading />}
    </>
  );
}
